import styled from "@emotion/styled";
import { ThemeKey } from "@smartsheet/design-tokens";
import { ReactNode } from "react";
import { sizes } from "../../../constants/spacing";
import { getButtonLoadingStyles } from "../styled/getButtonLoadingStyles";
import { ursaPadding } from "../styled/getButtonPadding";
import { mapButtonAppearance } from "../styled/mapButtonAppearance";
import { ButtonAppearances, ButtonSizes, LinkAppearances } from "../types";

interface Props {
    appearance: ButtonAppearances | LinkAppearances;
    hasLeftIcon: boolean;
    hasRightIcon: boolean;
    size: ButtonSizes;
    themeMode: ThemeKey;
    children?: ReactNode;
    clientId?: string;
    hideEmptyContent?: boolean;
    isLoading?: boolean;
    isSplit?: boolean;
}

const getMargin = (
    hasLeftIcon: boolean,
    hasRightIcon: boolean,
    appearance: ButtonAppearances | LinkAppearances,
    size: ButtonSizes
) => {
    switch (appearance) {
        // For Lodestar buttons.
        case "borderless":
        case "primary":
        case "secondary":
        case "destructive":
        case "secondary-destructive":
        case "share":
            // Icons already provide a margin, so the content is only responsible for edges without an icon
            if (hasLeftIcon && hasRightIcon) {
                return { margin: `0` };
            }
            if (hasLeftIcon) {
                return { marginRight: `var(--sds--button--gap--${size}, ${sizes.small - 1}px)` }; // -1px for border
            }
            if (hasRightIcon) {
                return { marginLeft: `var(--sds--button--gap--${size}, ${sizes.small - 1}px)` };
            }
            // Proper spacing will be provided by the button padding if there are no icons present
            return { margin: `0` };
    }
};
const getPadding = (appearance: ButtonAppearances | LinkAppearances, size: ButtonSizes, themeMode: ThemeKey) => {
    const actualAppearance = mapButtonAppearance(appearance as ButtonAppearances | "link", themeMode);

    switch (actualAppearance) {
        // For Ursa buttons.
        case "primary-filled":
        case "primary-empty":
        case "secondary-filled":
        case "secondary-outlined":
        case "secondary-empty":
        case "canvas-filled":
        case "canvas-empty":
        case "destructive-filled":
        case "destructive-empty":
            return {
                paddingLeft: `var(--sds--button--gap--${size}, ${ursaPadding[size]})`,
                paddingRight: `var(--sds--button--gap--${size}, ${ursaPadding[size]})`,
            };
    }
};

export const Content = styled("span")<Props>`
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    ${({ isLoading }) => getButtonLoadingStyles(isLoading)};
    ${({ hasLeftIcon, hasRightIcon, appearance, size, themeMode }) =>
        themeMode.includes("lodestar") && getMargin(hasLeftIcon, hasRightIcon, appearance, size)};
    ${({ appearance, size, themeMode }) => getPadding(appearance, size, themeMode)};

    ${({ hideEmptyContent }) =>
        hideEmptyContent &&
        `
        &:empty, &:has(*:empty) {
            display: none;
        }
    `};
`;
